import { formatPrice, formatSGTDateTime, maskName } from "@luce/core";

import {
  CreditAccountTransactionTypeEnum,
  PackageDepartmentEnum,
  WorkerDepartmentEnum,
} from "../__generated__/graphql";
import { BACK_TO } from "../shared/lib";
import EnvelopeMinImg from "./assets/envelope-min.png";
import EnvelopePlusImg from "./assets/envelope-plus.png";
import HomeBeautyImg from "./assets/home-beauty.png";
import HomeMassageImg from "./assets/home-massage.png";
import PetSittingImg from "./assets/pet-sitting.png";
import TransactionAdjustmentImg from "./assets/transaction-adjustment.png";
import TransactionEmptyImg from "./assets/transaction-empty.png";
import TransactionGiftImg from "./assets/transaction-gift.png";
import TransactionPaymentImg from "./assets/transaction-payment.png";
import TransactionReferralImg from "./assets/transaction-referral.png";
import TransactionRefundImg from "./assets/transaction-refund.png";
import TransactionRibbonImg from "./assets/transaction-ribbon.png";
import TransactionWalletImg from "./assets/transaction-wallet.png";
import TransactionWithdrawImg from "./assets/transaction-withdraw.png";
import type { DepartmentCreditKey } from "./gqlhooks/use-client-credit-accounts";
import type { CreditHistoryTransaction } from "./gqlhooks/use-credit-account-detail";

export const PROFILE_PATH = "/profile";
export const MAIN_CREDIT_PATH = "/profile/credit-account/general";
export const MAIN_CREDIT_TRANSACTIONS_PATH = "/profile/credit-account/general/transactions";
export const PET_CREDIT_TRANSACTIONS_PATH = "/profile/credit-account/pet/transactions";

type DepartmentCreditConfig = {
  label: string;
  icon: string | number;
  path: string;
  transactionsPath: string;
};

export const DEPARTMENT_CREDIT_CONFIG = {
  massage: {
    label: "Massage",
    icon: HomeMassageImg,
    path: BACK_TO.massageCredit,
    transactionsPath: "/profile/credit-account/massage/transactions",
  },
  beauty: {
    label: "Beauty",
    icon: HomeBeautyImg,
    path: BACK_TO.beautyCredit,
    transactionsPath: "/profile/credit-account/beauty/transactions",
  },
  petCare: {
    label: "Pet Care",
    icon: PetSittingImg,
    path: BACK_TO.petCredit,
    transactionsPath: PET_CREDIT_TRANSACTIONS_PATH,
  },
} satisfies Record<DepartmentCreditKey, DepartmentCreditConfig>;

export const APPLY_CREDIT_HELP_URL = "https://help.lucemg.com/applying-a-credit";

export const CASH_OUT_WHATSAPP_MESSAGE =
  "Hi! I would like to speak to sales about booking a new appointment for Home Cleaning service";

export const creditDepartmentType = {
  MASSAGE: "MASSAGE",
  BEAUTY: "HOME_BEAUTY",
  PET: "PET_CARE",
} as const;

export type CreditDepartmentType = keyof typeof creditDepartmentType;

export const getCreditWorkerDepartment = (
  creditDepartment: CreditDepartmentType,
): WorkerDepartmentEnum => {
  switch (creditDepartment) {
    case "MASSAGE":
      return WorkerDepartmentEnum.Massage;
    case "BEAUTY":
      return WorkerDepartmentEnum.HomeBeauty;
    case "PET":
      return WorkerDepartmentEnum.PetCare;
    default:
      throw new Error(`Invalid credit worker department: ${creditDepartment}`);
  }
};
export const getCreditPackageDepartment = (
  creditDepartment: CreditDepartmentType,
): PackageDepartmentEnum => {
  switch (creditDepartment) {
    case "MASSAGE":
      return PackageDepartmentEnum.Massage;
    case "BEAUTY":
      return PackageDepartmentEnum.HomeBeauty;
    case "PET":
      return PackageDepartmentEnum.PetCare;
    default:
      throw new Error(`Invalid credit package department: ${creditDepartment}`);
  }
};

type CreditTransactionDisplay = {
  icon: string | number;
  title: string;
  description?: string;
  date?: string;
  amount: string;
  isDebit: boolean;
};

const TRANSACTION_DATE_FORMAT = "d MMM yyyy, hh:mm a";

function transactionIcon(transactionType: CreditAccountTransactionTypeEnum): string | number {
  switch (transactionType) {
    case CreditAccountTransactionTypeEnum.TopUp:
    case CreditAccountTransactionTypeEnum.InitialBalance:
    case CreditAccountTransactionTypeEnum.Overpayment:
      return TransactionWalletImg;
    case CreditAccountTransactionTypeEnum.ServiceCompensation:
    case CreditAccountTransactionTypeEnum.Reward:
      return TransactionRibbonImg;
    case CreditAccountTransactionTypeEnum.GiftCard:
      return TransactionGiftImg;
    case CreditAccountTransactionTypeEnum.Referral:
      return TransactionReferralImg;
    case CreditAccountTransactionTypeEnum.Payment:
      return TransactionPaymentImg;
    case CreditAccountTransactionTypeEnum.Refund:
      return TransactionRefundImg;
    case CreditAccountTransactionTypeEnum.TaxWriteOff:
    case CreditAccountTransactionTypeEnum.CreditAbsorption:
      return TransactionAdjustmentImg;
    case CreditAccountTransactionTypeEnum.CashWithdrawal:
      return TransactionWithdrawImg;
    default:
      return TransactionEmptyImg;
  }
}

function giftCardDisplay(
  tx: CreditHistoryTransaction,
  senderName: string | undefined,
): CreditTransactionDisplay {
  const isExpired = tx.transactionType === CreditAccountTransactionTypeEnum.CreditAbsorption;
  const dateValue = tx.authorizedAt ?? tx.createdAt;
  const expiresOn = tx.expirationDate
    ? `Expires on ${formatSGTDateTime(tx.expirationDate, "d MMM yyyy")}`
    : undefined;
  const from = senderName ? `From ${maskName(senderName)}` : undefined;

  return {
    icon: isExpired ? EnvelopeMinImg : EnvelopePlusImg,
    title: isExpired ? "Gift Card Expired" : "Gift Card Redeemed",
    description:
      from && expiresOn ? `${from}・${expiresOn}` : from || expiresOn || tx.description.trim(),
    date: dateValue ? formatSGTDateTime(dateValue, TRANSACTION_DATE_FORMAT) : undefined,
    amount: `${isExpired ? "-" : "+"}${formatPrice(Math.abs(tx.amount))}`,
    isDebit: isExpired,
  };
}

export function getCreditTransactionDisplay(
  tx: CreditHistoryTransaction,
): CreditTransactionDisplay {
  if (tx.linkedObject?.__typename === "GiftCard") {
    return giftCardDisplay(tx, tx.linkedObject.sender.name.trim());
  }

  return {
    icon: transactionIcon(tx.transactionType),
    title: tx.description,
    date: tx.authorizedAt ? formatSGTDateTime(tx.authorizedAt, TRANSACTION_DATE_FORMAT) : undefined,
    amount: `${tx.amount < 0 ? "-" : ""}${formatPrice(Math.abs(tx.amount))}`,
    isDebit: tx.transactionType === CreditAccountTransactionTypeEnum.Payment,
  };
}
